import { useEffect, type RefObject } from 'react';

import { getElementBounds } from '../../lib/bound';
import { getElementAtPosition } from '../../lib/collision';
import { sceneCoordsToViewportCoords } from '../../lib/coords';
import {
  getCursorForResizingElement,
  resolveDynamicPluginCursor,
  setCursor,
} from '../../lib/cursor';
import {
  defaultGenericElement,
  notifyElementsChanged,
  reversionElement,
  updateMultipleElements,
} from '../../lib/element';
import { history } from '../../lib/history';
import { getActionReferences, getPlugin, type Plugin } from '../../lib/plugin';
import {
  renderInteractiveScene,
  renderNewElementScene,
  renderStaticScene,
} from '../../lib/scene';
import {
  getElementsWithinSelection,
  getSelectedElements,
  isHittingAnySelectedElementHandle,
  isHittingCommonBoundingBoxOfSelectedElements,
  isPointInBounds,
  mergeSelectionIds,
  type TransformHandleType,
} from '../../lib/selection';
import { dragNewElement, transformElements } from '../../lib/transformation';
import {
  constrainPanToElement,
  getNormalizedZoom,
  getStateForZoom,
} from '../../lib/zoom';
import {
  handlePolyPointPointerDown,
  handlePolyPointPointerUp,
  handlePolyPointRectSelectMove,
} from '../../plugins/poly/selection';
import { updateCursorStore } from '../../stores/useCursorStore';
import {
  updateRuntimeStore,
  useEditorRuntimeStore,
  type RuntimeState,
} from '../../stores/useEditorRuntimeStore';
import { updateEditorStore, useEditorStore } from '../../stores/useEditorStore';
import { debounce } from '../../utils/debounce';
import { viewportCoordsToSceneCoords } from '../../utils/editor/coords';
import { parseMouseButton, parseMouseButtons } from '../../utils/events';
import { withThrottled } from '../../utils/react';

const lastViewportPosition = { x: 0, y: 0 };
const activePointers = new Map<number, { x: number; y: number }>(); // for multi touch actions
let previousDistance = -1;
let touchStartPos: { x: number; y: number } | null = null;
const TOUCH_TAP_THRESHOLD = 10;

const isBlockTouchPanningActive = (plugins: Plugin[]): boolean => {
  const { activeTool } = useEditorRuntimeStore.getState();
  return plugins.some(
    (p) => p.name === activeTool && p.events.blockTouchPanning,
  );
};

const resetShouldCacheIgnoreZoomDebounced = debounce(() => {
  updateRuntimeStore({ shouldCacheIgnoreZoom: false });
  renderNewElementScene();
  renderStaticScene();
}, 300);

const getViewTransformations = (runtimeState: RuntimeState) => {
  const { offsetLeft, offsetTop, scrollX, scrollY, zoom } = runtimeState;
  return {
    offsetLeft,
    offsetTop,
    scrollX,
    scrollY,
    zoom: zoom.value,
  };
};

type EventOptions = { disableCursorControl: boolean };

const handlePointerMove = (plugins: Plugin[], options: EventOptions) => {
  const throttledSinglePointer = withThrottled((original: PointerEvent) => {
    let runtimeState = useEditorRuntimeStore.getState();
    const elementState = useEditorStore.getState();
    const viewTransformations = getViewTransformations(runtimeState);

    const pos = viewportCoordsToSceneCoords(original, viewTransformations);
    const button = parseMouseButtons(original.buttons);

    updateCursorStore({ position: sceneCoordsToViewportCoords(pos) });

    plugins.forEach((plugin) => {
      plugin.events.onPointerMove?.({
        elementState,
        event: { button, original, pos },
        helpers: getActionReferences(),
        history,
        runtimeState,
      });
    });

    if (runtimeState.activeTool === 'selection') {
      let hitTransformHandle = false;
      const selectedElements = getSelectedElements();
      const isSingleSelection = selectedElements.length === 1;

      const element = isSingleSelection
        ? elementState.elements.get(elementState.selectedElementIds[0])!
        : undefined;

      if (!element?.isLocked || selectedElements.length > 1) {
        const plugin = isSingleSelection ? getPlugin(element!.type) : undefined;

        const resize = isHittingAnySelectedElementHandle(
          [pos.x, pos.y],
          selectedElements,
        );

        if (resize || runtimeState.activeTransformation) {
          hitTransformHandle = true;

          const currentHoveredId = useEditorStore.getState().hoveredElementId;
          if (currentHoveredId) {
            updateEditorStore({ hoveredElementId: null });
            renderInteractiveScene();
          }

          if (runtimeState.activeTransformation?.type === 'move') {
            if (!options.disableCursorControl && !runtimeState.readonly) {
              setCursor('move');
            }
          } else {
            const type = (resize ?? runtimeState.activeTransformation?.type)!;
            const cursor = plugin?.transformation.getCursorFromTransformation
              ? plugin.transformation.getCursorFromTransformation(type)
              : getCursorForResizingElement(
                  type as TransformHandleType,
                  element,
                );
            if (!runtimeState.readonly) setCursor(cursor);
          }
        }

        if (runtimeState.activeTransformation) {
          if (plugin?.transformation.transformElement && isSingleSelection) {
            plugin.transformation.transformElement(
              runtimeState.activeTransformation.type,
              selectedElements[0],
              runtimeState.activeTransformation.originalElements,
              [pos.x, pos.y],
              runtimeState.activeTransformation.origin,
              { elementState, runtimeState },
            );
          } else {
            transformElements(
              runtimeState.activeTransformation.type as TransformHandleType,
              selectedElements,
              runtimeState.activeTransformation.originalElements,
              [pos.x, pos.y],
              runtimeState.activeTransformation.origin,
            );
          }

          if (!runtimeState.activeTransformation.hasChanges) {
            runtimeState = updateRuntimeStore({
              activeTransformation: {
                ...runtimeState.activeTransformation,
                hasChanges: true,
              },
            });
          }

          renderInteractiveScene();
          renderStaticScene();
        }

        if (!hitTransformHandle) {
          const hitElement = getElementAtPosition(pos.x, pos.y);

          if (
            hitElement &&
            runtimeState.limitSelectionToIds.length &&
            !runtimeState.limitSelectionToIds.includes(hitElement.id)
          ) {
            return;
          }

          const currentHoveredId = useEditorStore.getState().hoveredElementId;
          if (hitElement && hitElement.id !== currentHoveredId) {
            updateEditorStore({ hoveredElementId: hitElement.id });
            renderInteractiveScene();
          } else if (!hitElement && currentHoveredId) {
            updateEditorStore({ hoveredElementId: null });
            renderInteractiveScene();
          }

          if (
            hitElement ||
            isHittingCommonBoundingBoxOfSelectedElements(
              [pos.x, pos.y],
              selectedElements,
            )
          ) {
            if (!options.disableCursorControl && !runtimeState.readonly) {
              setCursor('move');
            }
          } else {
            if (!options.disableCursorControl) setCursor('auto');
          }

          const selectionCursorOverride = resolveDynamicPluginCursor(plugin);
          if (
            selectionCursorOverride &&
            !options.disableCursorControl &&
            !runtimeState.readonly
          ) {
            setCursor(selectionCursorOverride);
          }
        }
      }
    }

    if (
      button === 'left' &&
      runtimeState.activeTool === 'selection' &&
      runtimeState.selectionElement &&
      elementState.polygonPointsVisible
    ) {
      handlePolyPointRectSelectMove(pos);
    }

    if (
      button === 'left' &&
      runtimeState.activeTool === 'selection' &&
      runtimeState.selectionElement &&
      !elementState.polygonPointsVisible
    ) {
      const elementsInSelection = getElementsWithinSelection();

      const newSelectedIds = elementsInSelection
        .filter((element) => !element.isLocked)
        .map((element) => element.id);

      updateEditorStore({
        selectedElementIds: mergeSelectionIds(
          newSelectedIds,
          elementState.selectedElementIds,
        ),
      });

      updateRuntimeStore({
        selectionElement: {
          ...runtimeState.selectionElement,
          ...dragNewElement({
            ...runtimeState.selectionElement,
            x: pos.x,
            y: pos.y,
          }),
        },
      });

      renderInteractiveScene();
    }

    if (
      runtimeState.activeTool === 'hand' &&
      button !== 'middle' &&
      button !== 'left' &&
      !options.disableCursorControl
    ) {
      setCursor('grab');
    }

    if (
      button === 'middle' ||
      (runtimeState.activeTool === 'hand' && button === 'left') ||
      (original.pointerType === 'touch' &&
        button === 'left' &&
        !isBlockTouchPanningActive(plugins))
    ) {
      if (!options.disableCursorControl) setCursor('grabbing');

      const deltaX = lastViewportPosition.x - original.clientX;
      const deltaY = lastViewportPosition.y - original.clientY;

      const targetScrollX =
        runtimeState.scrollX - deltaX / runtimeState.zoom.value;
      const targetScrollY =
        runtimeState.scrollY - deltaY / runtimeState.zoom.value;

      updateRuntimeStore(
        constrainPanToElement(
          targetScrollX,
          targetScrollY,
          runtimeState.panConstraintElementId,
          window.innerWidth,
          window.innerHeight,
          runtimeState.zoom.value,
        ),
      );

      renderNewElementScene();
      renderStaticScene();
      renderInteractiveScene();
    }

    lastViewportPosition.x = original.clientX;
    lastViewportPosition.y = original.clientY;
  });

  return (original: PointerEvent) => {
    if (activePointers.has(original.pointerId)) {
      activePointers.set(original.pointerId, {
        x: original.clientX,
        y: original.clientY,
      });

      if (activePointers.size === 2) {
        const pointers = Array.from(activePointers.values());
        const currentDistance = Math.hypot(
          pointers[0].x - pointers[1].x,
          pointers[0].y - pointers[1].y,
        );

        if (previousDistance > 0) {
          const runtimeState = useEditorRuntimeStore.getState();
          const newZoom =
            runtimeState.zoom.value * (currentDistance / previousDistance);

          const zoomState = getStateForZoom({
            nextZoom: getNormalizedZoom(newZoom),
            viewportX: (pointers[0].x + pointers[1].x) / 2,
            viewportY: (pointers[0].y + pointers[1].y) / 2,
          });

          updateRuntimeStore({
            ...zoomState,
            ...constrainPanToElement(
              zoomState.scrollX,
              zoomState.scrollY,
              runtimeState.panConstraintElementId,
              window.innerWidth,
              window.innerHeight,
              zoomState.zoom.value,
            ),
            shouldCacheIgnoreZoom: true,
          });

          resetShouldCacheIgnoreZoomDebounced();
        }

        previousDistance = currentDistance;

        renderInteractiveScene();
        renderStaticScene();
        return;
      }
    }

    throttledSinglePointer(original);
  };
};

const handlePointerDown = (
  plugins: Plugin[],
  options: EventOptions,
  canvas: HTMLCanvasElement | null,
) => {
  return (original: PointerEvent) => {
    if (canvas == null) return;

    activePointers.set(original.pointerId, {
      x: original.clientX,
      y: original.clientY,
    });
    canvas.setPointerCapture(original.pointerId);

    if (activePointers.size > 2) return;
    if (activePointers.size === 2) {
      touchStartPos = null; // pinch started, cancel any pending tap
      const pointers = Array.from(activePointers.values());
      previousDistance = Math.hypot(
        pointers[0].x - pointers[1].x,
        pointers[0].y - pointers[1].y,
      );
      return;
    }

    if (
      original.pointerType === 'touch' &&
      !isBlockTouchPanningActive(plugins)
    ) {
      touchStartPos = { x: original.clientX, y: original.clientY };
      lastViewportPosition.x = original.clientX;
      lastViewportPosition.y = original.clientY;
      return;
    }

    withThrottled(async (original: PointerEvent) => {
      let runtimeState = useEditorRuntimeStore.getState();
      const elementState = useEditorStore.getState();

      const { readonly } = runtimeState;

      const viewTransformations = getViewTransformations(runtimeState);

      const button = parseMouseButtons(original.buttons);
      const pos = viewportCoordsToSceneCoords(original, viewTransformations);

      const singleSelectedElement =
        elementState.selectedElementIds.length === 1
          ? elementState.elements.get(elementState.selectedElementIds[0])
          : undefined;

      const hitSingleElementBoundingPrior =
        !!singleSelectedElement &&
        isPointInBounds(
          [pos.x, pos.y],
          getElementBounds(singleSelectedElement),
          5,
        );

      const pointerDownHandled = await plugins.reduce<Promise<boolean>>(
        async (accPromise, plugin) => {
          const alreadyHandled = await accPromise;
          if (alreadyHandled) return true;

          const result = await plugin.events.onPointerDown?.({
            elementState,
            event: { button, original, pos },
            helpers: getActionReferences(),
            history,
            runtimeState,
          });

          return result === true;
        },
        Promise.resolve(false),
      );

      runtimeState = useEditorRuntimeStore.getState();

      if (
        runtimeState.activeTool === 'selection' &&
        button === 'left' &&
        !pointerDownHandled
      ) {
        const selectedElements = getSelectedElements();
        const isSingleSelection = selectedElements.length === 1;

        if (
          elementState.polygonPointsVisible &&
          !readonly &&
          isSingleSelection &&
          getPlugin(selectedElements[0].type).selection?.hasVisiblePointHandles
        ) {
          handlePolyPointPointerDown(pos, original);
          renderInteractiveScene();
          return;
        }

        const element = isSingleSelection
          ? elementState.elements.get(elementState.selectedElementIds[0])!
          : undefined;

        if (!element?.isLocked || selectedElements.length > 1) {
          const resize = isHittingAnySelectedElementHandle(
            [pos.x, pos.y],
            selectedElements,
          );

          if (resize && !readonly) {
            runtimeState = updateRuntimeStore({
              activeTransformation: {
                hasChanges: false,
                origin: [pos.x, pos.y],
                originalElements: new Map(elementState.elements),
                type: resize,
              },
            });
            const resizeTypeIndex = Number.parseInt(resize.split('-')[0], 10);

            if (!Number.isNaN(resizeTypeIndex)) {
              updateEditorStore({
                selectedSubElementIds: [resizeTypeIndex.toString()],
              });
            } else {
              updateEditorStore({ selectedSubElementIds: [] });
            }
          } else {
            updateEditorStore({ selectedSubElementIds: [] });
          }

          const hitCommonSelectionBounds =
            isHittingCommonBoundingBoxOfSelectedElements(
              [pos.x, pos.y],
              selectedElements,
            );
          const hitElement = resize ? null : getElementAtPosition(pos.x, pos.y);

          // Early exit for restricted selection mode
          if (
            hitElement &&
            runtimeState.limitSelectionToIds.length &&
            !runtimeState.limitSelectionToIds.includes(hitElement.id)
          ) {
            return;
          }

          const isMultiSelect = original.ctrlKey || original.metaKey;

          const hitElementAlreadySelected =
            hitElement &&
            elementState.selectedElementIds.includes(hitElement.id);

          if (
            isMultiSelect &&
            hitElement &&
            hitElement.type !== 'boolean' &&
            hitElement.type !== 'grid' &&
            !resize
          ) {
            const hasBooleanSelected = elementState.selectedElementIds.some(
              (id) => elementState.elements.get(id)?.type === 'boolean',
            );

            updateEditorStore({
              selectedElementIds: hasBooleanSelected
                ? [hitElement.id]
                : hitElementAlreadySelected
                  ? elementState.selectedElementIds.filter(
                      (id) => id !== hitElement.id,
                    )
                  : [...elementState.selectedElementIds, hitElement.id],
              polygonPointsVisible: false,
            });
          } else if (
            hitElement &&
            hitElement.type !== 'boolean' &&
            !resize &&
            !hitCommonSelectionBounds &&
            !hitElementAlreadySelected &&
            !(
              hitSingleElementBoundingPrior && elementState.polygonPointsVisible
            )
          ) {
            updateEditorStore({
              selectedElementIds: [hitElement.id],
              polygonPointsVisible: !getPlugin(hitElement.type).selection
                ?.hasVisiblePointHandles,
            });
          }

          // Start a move transformation if we hit an element or the current selections bounding box
          // exclude boolean elements as they are managed by their children
          if (
            !readonly &&
            !isMultiSelect &&
            (hitElement || hitCommonSelectionBounds) &&
            !resize &&
            (!hitElement || hitElement.type !== 'boolean')
          ) {
            runtimeState = updateRuntimeStore({
              activeTransformation: {
                hasChanges: false,
                origin: [pos.x, pos.y],
                originalElements: new Map(elementState.elements),
                type: 'move',
              },
            });
          }

          // clicked on empty space -> start selection box (mouse only)
          if (
            !hitElement &&
            !runtimeState.activeTransformation &&
            original.pointerType !== 'touch' &&
            !(
              hitSingleElementBoundingPrior && elementState.polygonPointsVisible
            )
          ) {
            // clear existing selection
            if (elementState.selectedElementIds.length) {
              updateEditorStore({
                selectedElementIds: [],
                polygonPointsVisible: false,
              });
            }

            runtimeState = updateRuntimeStore({
              selectionElement: {
                ...defaultGenericElement(),
                originX: pos.x,
                originY: pos.y,
                type: 'selection',
                x: pos.x,
                y: pos.y,
              },
            });
          }

          renderInteractiveScene();
        }
      }

      lastViewportPosition.x = original.clientX;
      lastViewportPosition.y = original.clientY;
      if (button === 'middle') {
        if (!options.disableCursorControl) setCursor('grabbing');
      }
    })(original);
  };
};

const handlePointerUp = (plugins: Plugin[], options: EventOptions) => {
  return (original: PointerEvent) => {
    const wasTwoPointers = activePointers.size === 2;
    activePointers.delete(original.pointerId);
    previousDistance = -1;

    if (wasTwoPointers) {
      // Sync lastViewportPosition to the remaining finger so that panning
      // doesn't compute a stale delta on the first move after the pinch.
      const remaining = activePointers.values().next().value;
      if (remaining) {
        lastViewportPosition.x = remaining.x;
        lastViewportPosition.y = remaining.y;
      }
      // Clear any interaction state (selection box, transformation) that may
      // have been set by the first finger before the pinch started, so the
      // remaining finger doesn't resume them from a stale origin.
      updateRuntimeStore({
        selectionElement: undefined,
        activeTransformation: undefined,
      });
      return;
    }

    if (
      original.pointerType === 'touch' &&
      !isBlockTouchPanningActive(plugins)
    ) {
      if (touchStartPos) {
        const dx = original.clientX - touchStartPos.x;
        const dy = original.clientY - touchStartPos.y;

        if (Math.hypot(dx, dy) < TOUCH_TAP_THRESHOLD) {
          const runtimeState = useEditorRuntimeStore.getState();
          const elementState = useEditorStore.getState();
          const pos = viewportCoordsToSceneCoords(
            original,
            getViewTransformations(runtimeState),
          );

          plugins.forEach((plugin) => {
            plugin.events.onPointerDown?.({
              elementState,
              event: { button: 'left', original, pos },
              helpers: getActionReferences(),
              history,
              runtimeState,
            });
          });

          const hitElement = getElementAtPosition(pos.x, pos.y);

          if (hitElement && hitElement.type !== 'boolean') {
            updateEditorStore({
              selectedElementIds: [hitElement.id],
              polygonPointsVisible: !getPlugin(hitElement.type).selection
                ?.hasVisiblePointHandles,
            });
          } else if (!hitElement) {
            updateEditorStore({
              selectedElementIds: [],
              polygonPointsVisible: false,
            });
          }
          renderInteractiveScene();
        }
      }
      touchStartPos = null;
      return;
    }

    withThrottled((original: PointerEvent) => {
      const runtimeState = useEditorRuntimeStore.getState();
      const elementState = useEditorStore.getState();

      const { readonly } = runtimeState;

      const viewTransformations = getViewTransformations(runtimeState);

      const button = parseMouseButton(original.button);
      const pos = viewportCoordsToSceneCoords(original, viewTransformations);
      plugins.forEach((plugin) => {
        plugin.events.onPointerUp?.({
          elementState,
          event: { button, original, pos },
          helpers: getActionReferences(),
          history,
          runtimeState,
        });
      });

      if (runtimeState.newElement) return;

      if (runtimeState.selectionElement && elementState.polygonPointsVisible) {
        handlePolyPointPointerUp(pos);
      } else if (runtimeState.selectionElement) {
        const elementsInSelection = getElementsWithinSelection();
        const newSelectedIds = elementsInSelection
          .filter((element) => !element.isLocked)
          .map((element) => element.id);
        updateEditorStore({
          selectedElementIds: mergeSelectionIds(
            newSelectedIds,
            elementState.selectedElementIds,
          ),
        });
        updateRuntimeStore({ selectionElement: undefined });
        renderInteractiveScene();
      }

      if (runtimeState.activeTool === 'hand' && !options.disableCursorControl) {
        setCursor('grab');
      }

      if (runtimeState.activeTransformation?.hasChanges) {
        history.push(elementState);
      }

      const selectedElements = getSelectedElements();

      updateRuntimeStore({ activeTransformation: undefined });

      if (!readonly) {
        updateMultipleElements(
          elementState.elements,
          selectedElements,
          selectedElements.map((element) => ({
            originX: element.x,
            originY: element.y,
            shape: getPlugin(element.type).collision.calculateShape(element),
            ...reversionElement(element, ['bounds', 'canvas', 'path']),
          })),
        );
        notifyElementsChanged();
      }

      renderInteractiveScene();
      renderStaticScene();
    })(original);
  };
};

const handleDoubleClick = () => {
  return (original: MouseEvent) => {
    const runtimeState = useEditorRuntimeStore.getState();
    const elementState = useEditorStore.getState();

    const { readonly } = runtimeState;

    if (runtimeState.activeTool !== 'selection' || readonly) return;

    const viewTransformations = getViewTransformations(runtimeState);

    const pos = viewportCoordsToSceneCoords(original, viewTransformations);
    const hitElement = getElementAtPosition(pos.x, pos.y);

    if (
      hitElement &&
      getPlugin(hitElement.type).selection?.hasVisiblePointHandles &&
      elementState.selectedElementIds.includes(hitElement.id)
    ) {
      history.push(
        updateEditorStore({
          polygonPointsVisible: !elementState.polygonPointsVisible,
        }),
      );
      renderInteractiveScene();
    }
  };
};

const handleWheel = () => {
  return withThrottled((e: WheelEvent) => {
    const runtimeState = useEditorRuntimeStore.getState();

    const { deltaY, clientX, clientY } = e;
    const sign = Math.sign(deltaY);
    const roundedZoom = Math.round(runtimeState.zoom.value * 10) / 10;
    const newZoom = roundedZoom + (sign > 0 ? -0.1 : 0.1);

    const zoomState = getStateForZoom({
      nextZoom: getNormalizedZoom(newZoom),
      viewportX: clientX,
      viewportY: clientY,
    });

    updateRuntimeStore({
      ...zoomState,
      ...constrainPanToElement(
        zoomState.scrollX,
        zoomState.scrollY,
        runtimeState.panConstraintElementId,
        window.innerWidth,
        window.innerHeight,
        zoomState.zoom.value,
      ),
      shouldCacheIgnoreZoom: true,
    });

    resetShouldCacheIgnoreZoomDebounced();

    renderNewElementScene();
    renderStaticScene();
    renderInteractiveScene();
  });
};

export const useEditorMouseEvents = (
  plugins: Plugin[],
  disableCursorControl: boolean,
  interactiveCanvasRef: RefObject<HTMLCanvasElement | null>,
) => {
  useEffect(() => {
    const options: EventOptions = { disableCursorControl };

    const canvas = interactiveCanvasRef.current;

    const move = handlePointerMove(plugins, options);
    const down = handlePointerDown(plugins, options, canvas);
    const up = handlePointerUp(plugins, options);
    const wheel = handleWheel();
    const doubleClick = handleDoubleClick();

    if (interactiveCanvasRef && canvas) {
      interactiveCanvasRef.current?.addEventListener('pointermove', move);
      interactiveCanvasRef.current?.addEventListener('pointerdown', down);
      interactiveCanvasRef.current?.addEventListener('pointerup', up);
      interactiveCanvasRef.current?.addEventListener('dblclick', doubleClick);
      interactiveCanvasRef.current?.addEventListener(
        'wheel',
        (e) => {
          e.preventDefault();
          wheel(e);
        },
        { passive: false },
      );
    }

    return () => {
      canvas?.removeEventListener('pointermove', move);
      canvas?.removeEventListener('pointerdown', down);
      canvas?.removeEventListener('dblclick', doubleClick);
      canvas?.removeEventListener('pointerup', up);
      canvas?.removeEventListener('wheel', wheel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
